import React, {useMemo} from 'react';
import {observer} from 'mobx-react';
import {EPBorderType, PCS_CSS, ProductCustomizationState} from './render.def';
import * as util from '../../util';
import {FrameNone} from './frame-none';
import {FrameOld} from './frame-old';
import {PrimaryMatWrapper} from '../components/engraved-plates/ep-extended/primary-mat-wrapper';
import {EPContent} from '../components/engraved-plates/ep-extended/ep-content';
// IMPORTANT: MUST NOT import from s.ts (product store) - this renderer code is used in multiple apps for image rendering

declare var __high_res: boolean;
declare var __image_gen: boolean;
declare var __product_page: boolean;

interface IProductRenderProps {
    css: PCS_CSS;
    zoom: boolean;
    pcs: ProductCustomizationState;
}

const ProductRender: React.FC<IProductRenderProps> = observer(({css, zoom, pcs}) => {
    const randomInt = useMemo(() => Math.floor(Math.random() * 1000000000), []);

    if (!css || !pcs) {
        return null;
    }

    if (pcs.CustomizationMethodID < 1) {
        return <FrameNone pcs={pcs} />;
    } else if (pcs.CustomizationMethodID === 1) {
        return <FrameOld pcs={pcs} />;
    } else {
        const isInnerAccent = pcs.EPBorderType === EPBorderType.InnerAccent;
        const bevelStyle: React.CSSProperties = {
            backgroundColor: css.ep_bevel_backgroundColor,
            left: css.ep_bevel_left,
            top: css.ep_bevel_top,
            width: css.ep_bevel_width,
            height: css.ep_bevel_height,
        };
        const primaryMatStyle: React.CSSProperties = {
            backgroundImage: css.ep_pmat_backgroundImage,
            left: css.ep_pmat_left,
            top: css.ep_pmat_top,
            transform: isInnerAccent ? 'translate(-50%, -50%)' : undefined,
            width: css.ep_pmat_width,
            height: css.ep_pmat_height,
            border: isInnerAccent ? css.ep_pmat_border : undefined,
        };

        return (
            <div
                id={`frame-outer-${randomInt}`}
                className="frame-outer"
                role="img"
                aria-label={pcs.ADA}
            >
                <div
                    id="frame-helper"
                    style={{
                        width: `${css.body_width}`,
                        height: `${css.body_height}`,
                    }}
                />

                {pcs.ProductID === 14631 && (
                    <div
                        style={{
                            width: `${parseInt(css.m_topleft_width.replace('px', ''), 10) * 2 + parseInt(css.m_top_width.replace('px', ''))}px`,
                            height: `${parseInt(css.m_topleft_width.replace('px', ''), 10) * 2 + parseInt(css.m_left_height.replace('px', ''))}px`,
                            boxShadow: 'rgb(180, 180, 180) 6px 6px 10px',
                            margin: '0 auto',
                            position: 'absolute',
                            top: 0,
                        }}
                    ></div>
                )}

                <div
                    id="frame-display"
                    style={{
                        width: `${css.body_width}`,
                        height: `${css.body_height}`,
                    }}
                >
                    <div
                        id="frame-container"
                        style={{
                            backgroundImage: `url("${css.pmat_BackgroundImage}")`,
                            width: css.pmat_width,
                            height: css.pmat_height,
                            backgroundSize: css.pmat_backgroundSize,
                            top: css.pmat_top,
                        }}
                    >
                        {/* Straight sides overlap the corners by 1px to cover subpixel seams
                            during scaling, without changing the frame's calculated dimensions. */}
                        <picture>
                            {!__high_res && (
                                <source
                                    type="image/webp"
                                    srcSet={util.webp(css.m_topleft_src)}
                                />
                            )}
                            <img
                                id="m_topleft"
                                alt=""
                                src={css.m_topleft_src}
                                style={{
                                    left: css.m_topleft_left,
                                    top: css.m_topleft_top,
                                    width: css.m_topleft_width,
                                }}
                            />
                        </picture>

                        <picture>
                            {!__high_res && (
                                <source
                                    type="image/webp"
                                    srcSet={util.webp(css.m_topright_src)}
                                />
                            )}
                            <img
                                id="m_topright"
                                alt=""
                                src={css.m_topright_src}
                                style={{
                                    left: css.m_topright_left,
                                    top: css.m_topright_top,
                                    width: css.m_topright_width,
                                }}
                            />
                        </picture>

                        <picture>
                            {!__high_res && (
                                <source
                                    type="image/webp"
                                    srcSet={util.webp(css.m_bottomright_src)}
                                />
                            )}
                            <img
                                id="m_bottomright"
                                alt=""
                                src={css.m_bottomright_src}
                                style={{
                                    left: css.m_bottomright_left,
                                    top: css.m_bottomright_top,
                                    width: css.m_bottomright_width,
                                }}
                            />
                        </picture>

                        <picture>
                            {!__high_res && (
                                <source
                                    type="image/webp"
                                    srcSet={util.webp(css.m_bottomleft_src)}
                                />
                            )}
                            <img
                                id="m_bottomleft"
                                alt=""
                                src={css.m_bottomleft_src}
                                style={{
                                    left: css.m_bottomleft_left,
                                    top: css.m_bottomleft_top,
                                    width: css.m_bottomleft_width,
                                }}
                            />
                        </picture>

                        <picture>
                            {!__high_res && (
                                <source
                                    type="image/webp"
                                    srcSet={util.webp(css.m_left_src)}
                                />
                            )}
                            <img
                                id="m_left"
                                alt=""
                                src={css.m_left_src}
                                style={{
                                    left: css.m_left_left,
                                    top: `calc(${css.m_left_top} - 1px)`,
                                    width: css.m_left_width,
                                    height: `calc(${css.m_left_height} + 2px)`,
                                }}
                            />
                        </picture>

                        <picture>
                            {!__high_res && (
                                <source
                                    type="image/webp"
                                    srcSet={util.webp(css.m_top_src)}
                                />
                            )}
                            <img
                                id="m_top"
                                alt=""
                                src={css.m_top_src}
                                style={{
                                    left: `calc(${css.m_top_left} - 1px)`,
                                    top: css.m_top_top,
                                    width: `calc(${css.m_top_width} + 2px)`,
                                    height: css.m_top_height,
                                }}
                            />
                        </picture>

                        <picture>
                            {!__high_res && (
                                <source
                                    type="image/webp"
                                    srcSet={util.webp(css.m_right_src)}
                                />
                            )}
                            <img
                                id="m_right"
                                alt=""
                                src={css.m_right_src}
                                style={{
                                    left: css.m_right_left,
                                    top: `calc(${css.m_right_top} - 1px)`,
                                    width: css.m_right_width,
                                    height: `calc(${css.m_right_height} + 2px)`,
                                }}
                            />
                        </picture>

                        <picture>
                            {!__high_res && (
                                <source
                                    type="image/webp"
                                    srcSet={util.webp(css.m_bottom_src)}
                                />
                            )}
                            <img
                                id="m_bottom"
                                alt=""
                                src={css.m_bottom_src}
                                style={{
                                    left: `calc(${css.m_bottom_left} - 1px)`,
                                    top: css.m_bottom_top,
                                    width: `calc(${css.m_bottom_width} + 2px)`,
                                    height: css.m_bottom_height,
                                }}
                            />
                        </picture>

                        {/* Mat Overlay (Dimensions Plus, type 75): the product's fixed print over the primary mat box. It sits after the moulding and
                            before the document stack, so the inner mats, the document and everything drawn after them paint over it */}
                        {css.mat_overlay_visible && (
                            <picture>
                                {!__high_res && (
                                    <source
                                        type="image/webp"
                                        srcSet={util.webp(css.mat_overlay_src)}
                                    />
                                )}
                                <img
                                    id="mat_overlay"
                                    alt=""
                                    src={css.mat_overlay_src}
                                    style={{
                                        left: css.mat_overlay_left,
                                        top: css.mat_overlay_top,
                                        width: css.mat_overlay_width,
                                        height: css.mat_overlay_height,
                                        objectFit: 'cover',
                                        pointerEvents: 'none',
                                    }}
                                />
                            </picture>
                        )}

                        {css.doc1_visible && (
                            <div
                                id="doc1_pmat_bevel"
                                style={{
                                    backgroundColor: css.doc1_pmat_bevel_backgroundColor,
                                    left: css.doc1_pmat_bevel_left,
                                    top: css.doc1_pmat_bevel_top,
                                    width: css.doc1_pmat_bevel_width,
                                    height: css.doc1_pmat_bevel_height,
                                }}
                            >
                                <div
                                    id="doc1_smat"
                                    style={{
                                        backgroundColor: css.doc1_smat_backgroundColor,
                                        left: css.doc1_smat_left,
                                        top: css.doc1_smat_top,
                                        width: css.doc1_smat_width,
                                        height: css.doc1_smat_height,
                                    }}
                                >
                                    <div
                                        id="doc1_smat_bevel"
                                        style={{
                                            backgroundColor: css.doc1_smat_bevel_backgroundColor,
                                            left: css.doc1_smat_bevel_left,
                                            top: css.doc1_smat_bevel_top,
                                            width: css.doc1_smat_bevel_width,
                                            height: css.doc1_smat_bevel_height,
                                        }}
                                    >
                                        {/* Mat 3 (Dimensions Plus, type 75): the third mat and its bevel between the inner accent and the document; collapsed (0px, no size) on every other frame */}
                                        <div
                                            id="doc1_mat3"
                                            style={{
                                                backgroundColor: css.doc1_mat3_backgroundColor,
                                                left: css.doc1_mat3_left,
                                                top: css.doc1_mat3_top,
                                                width: css.doc1_mat3_width,
                                                height: css.doc1_mat3_height,
                                            }}
                                        >
                                            <div
                                                id="doc1_mat3_bevel"
                                                style={{
                                                    backgroundColor: css.doc1_mat3_bevel_backgroundColor,
                                                    left: css.doc1_mat3_bevel_left,
                                                    top: css.doc1_mat3_bevel_top,
                                                    width: css.doc1_mat3_bevel_width,
                                                    height: css.doc1_mat3_bevel_height,
                                                }}
                                            >
                                                <picture>
                                                    {!pcs.image_uploaded && !__high_res && (
                                                        <source
                                                            type="image/webp"
                                                            srcSet={util.webp(zoom ? css.doc1_src.replace('_350', '_original') : css.doc1_src)}
                                                        />
                                                    )}
                                                    <img
                                                        id="doc1"
                                                        style={{
                                                            left: css.doc1_left,
                                                            top: css.doc1_top,
                                                            width: css.doc1_width,
                                                            height: css.doc1_height,
                                                            // Uploaded images can be off the document's aspect ratio (CAF doc
                                                            // size is entered after the crop modal). The server center-crops
                                                            // the stored o/ and d/ images to the doc ratio, so center-crop the
                                                            // local preview the same way instead of stretching it.
                                                            objectFit: pcs.image_uploaded ? 'cover' : undefined,
                                                        }}
                                                        src={zoom ? css.doc1_src.replace('_350', '_original') : css.doc1_src}
                                                        alt=""
                                                    />
                                                </picture>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {css.doc2_visible && (
                            <div
                                id="doc2_pmat_bevel"
                                style={{
                                    backgroundColor: css.doc2_pmat_bevel_backgroundColor,
                                    left: css.doc2_pmat_bevel_left,
                                    top: css.doc2_pmat_bevel_top,
                                    width: css.doc2_pmat_bevel_width,
                                    height: css.doc2_pmat_bevel_height,
                                }}
                            >
                                <div
                                    id="doc2_smat"
                                    style={{
                                        backgroundColor: css.doc2_smat_backgroundColor,
                                        left: css.doc2_smat_left,
                                        top: css.doc2_smat_top,
                                        width: css.doc2_smat_width,
                                        height: css.doc2_smat_height,
                                    }}
                                >
                                    <div
                                        id="doc2_smat_bevel"
                                        style={{
                                            backgroundColor: css.doc2_smat_bevel_backgroundColor,
                                            left: css.doc2_smat_bevel_left,
                                            top: css.doc2_smat_bevel_top,
                                            width: css.doc2_smat_bevel_width,
                                            height: css.doc2_smat_bevel_height,
                                        }}
                                    >
                                        <img
                                            id="doc2"
                                            loading="lazy"
                                            style={{
                                                left: css.doc2_left,
                                                top: css.doc2_top,
                                                width: css.doc2_width,
                                                height: css.doc2_height,
                                            }}
                                            src={zoom ? css.doc2_src.replace('_350', '_original') : css.doc2_src}
                                            alt=""
                                        />
                                    </div>
                                </div>
                            </div>
                        )}

                        {css.tassel_visible && (
                            <div
                                id="tassel_pmat_bevel"
                                style={{
                                    left: css.tassel_pmat_bevel_left,
                                    top: css.tassel_pmat_bevel_top,
                                    width: css.tassel_pmat_bevel_width,
                                    height: css.tassel_pmat_bevel_height,
                                    backgroundColor: css.tassel_pmat_bevel_backgroundColor,
                                }}
                            >
                                <div
                                    id="tassel_smat"
                                    style={{
                                        left: css.tassel_smat_left,
                                        top: css.tassel_smat_top,
                                        width: css.tassel_smat_width,
                                        height: css.tassel_smat_height,
                                        backgroundColor: css.tassel_smat_backgroundColor,
                                    }}
                                >
                                    <div
                                        id="tassel_smat_bevel"
                                        style={{
                                            left: css.tassel_smat_bevel_left,
                                            top: css.tassel_smat_bevel_top,
                                            width: css.tassel_smat_bevel_width,
                                            height: css.tassel_smat_bevel_height,
                                            backgroundColor: css.tassel_smat_bevel_backgroundColor,
                                        }}
                                    >
                                        <img
                                            id="tassel"
                                            style={{
                                                left: css.tassel_left,
                                                top: css.tassel_top,
                                                width: css.tassel_width,
                                                height: css.tassel_height,
                                            }}
                                            src={css.tassel_src}
                                            alt=""
                                        />
                                        <div
                                            style={{
                                                boxShadow: 'inset 0 0px 4px #000',
                                                left: css.tassel_left,
                                                top: css.tassel_top,
                                                width: css.tassel_width,
                                                height: css.tassel_height,
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>
                        )}

                        {css.fillet_visible && (
                            <div
                                id="f_container"
                                style={{
                                    left: css.f_container_left,
                                    top: css.f_container_top,
                                    width: css.f_container_width,
                                    height: css.f_container_height,
                                }}
                            >
                                <img
                                    id="f_topleft"
                                    alt=""
                                    src={css.f_topleft_src}
                                    style={{
                                        left: css.f_topleft_left,
                                        top: css.f_topleft_top,
                                        width: css.f_topleft_width,
                                    }}
                                />
                                <img
                                    id="f_topright"
                                    alt=""
                                    src={css.f_topright_src}
                                    style={{
                                        left: css.f_topright_left,
                                        top: css.f_topright_top,
                                        width: css.f_topright_width,
                                    }}
                                />
                                <img
                                    id="f_bottomright"
                                    alt=""
                                    src={css.f_bottomright_src}
                                    style={{
                                        left: css.f_bottomright_left,
                                        top: css.f_bottomright_top,
                                        width: css.f_bottomright_width,
                                    }}
                                />
                                <img
                                    id="f_bottomleft"
                                    alt=""
                                    src={css.f_bottomleft_src}
                                    style={{
                                        left: css.f_bottomleft_left,
                                        top: css.f_bottomleft_top,
                                        width: css.f_bottomleft_width,
                                    }}
                                />
                                <img
                                    id="f_left"
                                    alt=""
                                    src={css.f_left_src}
                                    style={{
                                        left: css.f_left_left,
                                        top: css.f_left_top,
                                        width: css.f_left_width,
                                        height: css.f_left_height,
                                    }}
                                />
                                <img
                                    id="f_top"
                                    alt=""
                                    src={css.f_top_src}
                                    style={{
                                        left: css.f_top_left,
                                        top: css.f_top_top,
                                        width: css.f_top_width,
                                        height: css.f_top_height,
                                    }}
                                />
                                <img
                                    id="f_right"
                                    alt=""
                                    src={css.f_right_src}
                                    style={{
                                        left: css.f_right_left,
                                        top: css.f_right_top,
                                        width: css.f_right_width,
                                        height: css.f_right_height,
                                    }}
                                />
                                <img
                                    id="f_bottom"
                                    alt=""
                                    src={css.f_bottom_src}
                                    style={{
                                        left: css.f_bottom_left,
                                        top: css.f_bottom_top,
                                        width: css.f_bottom_width,
                                        height: css.f_bottom_height,
                                    }}
                                />
                            </div>
                        )}

                        {/* Icon Founders Edition (type 92): the printed stripe on the centre of the visible primary mat. It sits before the
                            insignias, so the two corner marks, the lino and the plate paint over it */}
                        {css.founders_visible && (
                            <div
                                id="founders_top"
                                style={{
                                    backgroundColor: css.founders_line_bg,
                                    left: css.founders_top_left,
                                    top: css.founders_top_top,
                                    width: css.founders_top_width,
                                    height: css.founders_top_height,
                                }}
                            />
                        )}
                        {css.founders_visible && (
                            <div
                                id="founders_right"
                                style={{
                                    backgroundColor: css.founders_line_bg,
                                    left: css.founders_right_left,
                                    top: css.founders_right_top,
                                    width: css.founders_right_width,
                                    height: css.founders_right_height,
                                }}
                            />
                        )}
                        {css.founders_visible && (
                            <div
                                id="founders_bottom"
                                style={{
                                    backgroundColor: css.founders_line_bg,
                                    left: css.founders_bottom_left,
                                    top: css.founders_bottom_top,
                                    width: css.founders_bottom_width,
                                    height: css.founders_bottom_height,
                                }}
                            />
                        )}
                        {css.founders_visible && (
                            <div
                                id="founders_left"
                                style={{
                                    backgroundColor: css.founders_line_bg,
                                    left: css.founders_left_left,
                                    top: css.founders_left_top,
                                    width: css.founders_left_width,
                                    height: css.founders_left_height,
                                }}
                            />
                        )}

                        {css.insignia_visible && (
                            <picture>
                                {!__high_res && (
                                    <source
                                        type="image/webp"
                                        srcSet={util.webp(css.insignia_src)}
                                    />
                                )}
                                <img
                                    id="insignia"
                                    style={{
                                        left: css.insignia_left,
                                        top: css.insignia_top,
                                        width: css.insignia_width,
                                        height: css.insignia_height,
                                    }}
                                    src={css.insignia_src}
                                    alt=""
                                />
                            </picture>
                        )}

                        {css.insignia_bot_visible && (
                            <img
                                id="insignia"
                                style={{
                                    left: css.insignia_left,
                                    bottom: css.insignia_bot,
                                    width: css.insignia_width,
                                    height: css.insignia_height,
                                }}
                                src={css.insignia_src}
                                alt=""
                            />
                        )}

                        {css.insignia2_visible && (
                            <picture>
                                {!__high_res && (
                                    <source
                                        type="image/webp"
                                        srcSet={util.webp(css.insignia2_src)}
                                    />
                                )}
                                <img
                                    id="insignia2"
                                    style={{
                                        left: css.insignia2_left,
                                        top: css.insignia2_top,
                                        width: css.insignia2_width,
                                        height: css.insignia2_height,
                                    }}
                                    src={css.insignia2_src}
                                    alt=""
                                />
                            </picture>
                        )}

                        {css.lino_visible && (
                            <picture>
                                {!__high_res && (
                                    <source
                                        type="image/webp"
                                        srcSet={util.webp(css.lino_src)}
                                    />
                                )}
                                <img
                                    id="lino"
                                    style={{
                                        left: css.lino_left,
                                        top: css.lino_top,
                                        width: css.lino_width,
                                        height: css.lino_height,
                                    }}
                                    src={css.lino_src}
                                    alt=""
                                />
                            </picture>
                        )}

                        {css.ep_visible && (
                            <div
                                id="ep_bevel"
                                style={bevelStyle}
                            >
                                <PrimaryMatWrapper
                                    css={css}
                                    isInnerAccent={isInnerAccent}
                                >
                                    <div
                                        id="ep_pmat"
                                        style={primaryMatStyle}
                                    >
                                        <EPContent
                                            pcs={pcs}
                                            css={css}
                                        />
                                    </div>
                                </PrimaryMatWrapper>
                            </div>
                        )}

                        {(css.uv_line_styles || []).map((ls, idx) =>
                            ls.visible ? (
                                <div
                                    key={`uv_line_${idx}`}
                                    id={`uv_line_${idx + 1}`}
                                    style={{
                                        position: 'absolute',
                                        left: ls.left,
                                        top: ls.top,
                                        width: ls.width,
                                        height: ls.height,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: ls.color || '#fff',
                                        fontFamily: ls.fontFamily,
                                        fontWeight: ls.fontWeight || 400,
                                        fontSize: ls.fontSize,
                                        lineHeight: ls.height,
                                        textAlign: 'center',
                                        overflow: 'hidden',
                                        whiteSpace: 'nowrap',
                                        pointerEvents: 'none',
                                        textShadow: '0 0 2px rgba(0, 0, 0, 0.55), 0 1px 2px rgba(0, 0, 0, 0.35)',
                                    }}
                                >
                                    {ls.text}
                                </div>
                            ) : null,
                        )}

                        {css.varsity_visible && (
                            <div
                                id="varsity_pmat_bevel"
                                style={{
                                    left: css.varsity_pmat_bevel_left,
                                    top: css.varsity_pmat_bevel_top,
                                    width: css.varsity_pmat_bevel_width,
                                    height: css.varsity_pmat_bevel_height,
                                    backgroundColor: css.varsity_pmat_bevel_backgroundColor,
                                }}
                            >
                                <div
                                    id="varsity_smat"
                                    style={{
                                        left: css.varsity_smat_left,
                                        top: css.varsity_smat_top,
                                        width: css.varsity_smat_width,
                                        height: css.varsity_smat_height,
                                        backgroundColor: css.varsity_smat_backgroundColor,
                                    }}
                                >
                                    <div
                                        id="varsity_smat_bevel"
                                        style={{
                                            left: css.varsity_smat_bevel_left,
                                            top: css.varsity_smat_bevel_top,
                                            width: css.varsity_smat_bevel_width,
                                            height: css.varsity_smat_bevel_height,
                                            backgroundColor: css.varsity_smat_bevel_backgroundColor,
                                        }}
                                    >
                                        <div
                                            id="varsity_bg"
                                            style={{
                                                left: css.varsity_bg_left,
                                                top: css.varsity_bg_top,
                                                width: css.varsity_bg_width,
                                                height: css.varsity_bg_height,
                                                backgroundColor: css.varsity_bg_backgroundColor,
                                            }}
                                        >
                                            <img
                                                id="varsity"
                                                style={{
                                                    left: css.varsity_left,
                                                    top: css.varsity_top,
                                                    width: css.varsity_width,
                                                    height: css.varsity_height,
                                                }}
                                                src={css.varsity_src}
                                                alt=""
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {css.oval_visible && (
                            <div
                                id="oval_pmat_bevel"
                                style={{
                                    borderRadius: css.oval_pmat_bevel_borderRadius,
                                    left: css.oval_pmat_bevel_left,
                                    top: css.oval_pmat_bevel_top,
                                    width: css.oval_pmat_bevel_width,
                                    height: css.oval_pmat_bevel_height,
                                    backgroundColor: css.oval_pmat_bevel_backgroundColor,
                                }}
                            >
                                <div
                                    id="oval_smat"
                                    style={{
                                        borderRadius: css.oval_smat_borderRadius,
                                        left: css.oval_smat_left,
                                        top: css.oval_smat_top,
                                        width: css.oval_smat_width,
                                        height: css.oval_smat_height,
                                        backgroundColor: css.oval_smat_backgroundColor,
                                    }}
                                >
                                    <div
                                        id="oval_smat_bevel"
                                        style={{
                                            borderRadius: css.oval_smat_bevel_borderRadius,
                                            left: css.oval_smat_bevel_left,
                                            top: css.oval_smat_bevel_top,
                                            width: css.oval_smat_bevel_width,
                                            height: css.oval_smat_bevel_height,
                                            backgroundColor: css.oval_smat_bevel_backgroundColor,
                                        }}
                                    >
                                        <img
                                            id="oval"
                                            style={{
                                                borderRadius: css.oval_borderRadius,
                                                left: css.oval_left,
                                                top: css.oval_top,
                                                width: css.oval_width,
                                                height: css.oval_height,
                                            }}
                                            src={css.oval_src}
                                            alt=""
                                        />
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Spirit */}
                        {css.spirit_visible && (
                            <div
                                id="spirit_lef"
                                style={{
                                    backgroundColor: '#FFF',
                                    left: css.spi_left_left,
                                    top: css.spi_left_top,
                                    width: css.spi_left_width,
                                    height: css.spi_left_height,
                                }}
                            />
                        )}
                        {css.spirit_visible && (
                            <div
                                id="spirit_rig"
                                style={{
                                    backgroundColor: '#FFF',
                                    right: css.spi_right_right,
                                    top: css.spi_right_top,
                                    width: css.spi_right_width,
                                    height: css.spi_right_height,
                                }}
                            />
                        )}
                        {css.spirit_visible && (
                            <div
                                id="spirit_top_1"
                                style={{
                                    backgroundColor: '#FFF',
                                    left: css.spi_top1_left,
                                    top: css.spi_top1_top,
                                    width: css.spi_top1_width,
                                    height: css.spi_top1_height,
                                }}
                            />
                        )}
                        {css.spirit_visible && (
                            <div
                                id="spirit_top_2"
                                style={{
                                    backgroundColor: '#FFF',
                                    right: css.spi_top2_right,
                                    top: css.spi_top2_top,
                                    width: css.spi_top2_width,
                                    height: css.spi_top2_height,
                                }}
                            />
                        )}
                        {css.spirit_visible && (
                            <div
                                id="spirit_bot_1"
                                style={{
                                    backgroundColor: '#FFF',
                                    left: css.spi_bot1_left,
                                    bottom: css.spi_bot1_bot,
                                    width: css.spi_bot1_width,
                                    height: css.spi_bot1_height,
                                }}
                            />
                        )}
                        {css.spirit_visible && (
                            <div
                                id="spirit_bot_2"
                                style={{
                                    backgroundColor: '#FFF',
                                    right: css.spi_bot2_right,
                                    bottom: css.spi_bot2_bot,
                                    width: css.spi_bot2_width,
                                    height: css.spi_bot2_height,
                                }}
                            />
                        )}

                        {css.spirit_visible && (
                            <div
                                id="spirit_smat_lef"
                                style={{
                                    backgroundColor: css.spirit_smat_bg,
                                    left: css.spi_smat_left_left,
                                    top: css.spi_smat_left_top,
                                    width: css.spi_smat_left_width,
                                    height: css.spi_smat_left_height,
                                }}
                            />
                        )}
                        {css.spirit_visible && (
                            <div
                                id="spirit_smat_rig"
                                style={{
                                    backgroundColor: css.spirit_smat_bg,
                                    right: css.spi_smat_right_right,
                                    top: css.spi_smat_right_top,
                                    width: css.spi_smat_right_width,
                                    height: css.spi_smat_right_height,
                                }}
                            />
                        )}
                        {css.spirit_visible && (
                            <div
                                id="spirit_smat_top_1"
                                style={{
                                    backgroundColor: css.spirit_smat_bg,
                                    left: css.spi_smat_top1_left,
                                    top: css.spi_smat_top1_top,
                                    width: css.spi_smat_top1_width,
                                    height: css.spi_smat_top1_height,
                                }}
                            />
                        )}
                        {css.spirit_visible && (
                            <div
                                id="spirit_smat_top_2"
                                style={{
                                    backgroundColor: css.spirit_smat_bg,
                                    right: css.spi_smat_top2_right,
                                    top: css.spi_smat_top2_top,
                                    width: css.spi_smat_top2_width,
                                    height: css.spi_smat_top2_height,
                                }}
                            />
                        )}
                        {css.spirit_visible && (
                            <div
                                id="spirit_smat_bot_1"
                                style={{
                                    backgroundColor: css.spirit_smat_bg,
                                    left: css.spi_smat_bot1_left,
                                    bottom: css.spi_smat_bot1_bot,
                                    width: css.spi_smat_bot1_width,
                                    height: css.spi_smat_bot1_height,
                                }}
                            />
                        )}
                        {css.spirit_visible && (
                            <div
                                id="spirit_smat_bot_2"
                                style={{
                                    backgroundColor: css.spirit_smat_bg,
                                    right: css.spi_smat_bot2_right,
                                    bottom: css.spi_smat_bot2_bot,
                                    width: css.spi_smat_bot2_width,
                                    height: css.spi_smat_bot2_height,
                                }}
                            />
                        )}

                        {/* Cameo */}
                        {css.cameo_visible && (
                            <div
                                id="cameo_pmat_bevel"
                                style={{
                                    borderRadius: css.cameo_pmat_bevel_borderRadius,
                                    left: css.cameo_pmat_bevel_left,
                                    top: css.cameo_pmat_bevel_top,
                                    width: css.cameo_pmat_bevel_width,
                                    height: css.cameo_pmat_bevel_height,
                                    backgroundColor: css.cameo_pmat_bevel_backgroundColor,
                                }}
                            >
                                <img
                                    id="cameo"
                                    style={{
                                        borderRadius: css.cameo_borderRadius,
                                        left: css.cameo_left,
                                        top: css.cameo_top,
                                        width: css.cameo_width,
                                        height: css.cameo_height,
                                    }}
                                    src={css.cameo_src}
                                    alt=""
                                />
                            </div>
                        )}

                        {pcs.ProductTypeID === 5 && pcs.PortalID !== 3 && !pcs.IsCAF && !__image_gen && __product_page && (
                            <div className="tassel-cord tassel-cord-2">
                                <picture>
                                    {!__high_res && (
                                        <source
                                            type="image/webp"
                                            srcSet={`https://${pcs.CDNDomain}/images/cached/general/honor-cord1.webp`}
                                        />
                                    )}
                                    <img
                                        width="128"
                                        height="128"
                                        src={`https://${pcs.CDNDomain}/images/cached/general/honor-cord1.png`}
                                        alt="Frame Also Holds Cord"
                                        loading="lazy"
                                    />
                                </picture>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }
});

export default ProductRender;
